import { cloneDeep } from "es-toolkit";

//#region ../../node_modules/@jwplayer/fe-common/module/poller/index.js
function createState(initialState) {
	let state = initialState;
	let subscriber;
	function getState() {
		return cloneDeep(state);
	}
	function setState(newState) {
		const stateChanged = state !== newState;
		state = newState;
		if (stateChanged) subscriber?.(getState());
	}
	function subscribe(fn) {
		subscriber = fn;
		return () => {
			subscriber = void 0;
		};
	}
	function when(fn) {
		return new Promise((resolve) => {
			if (fn(getState())) return resolve();
			subscribe((nextState) => {
				if (fn(nextState)) return resolve();
			});
		});
	}
	return {
		getState,
		setState,
		when,
		subscribe
	};
}
function poll(args, opts) {
	const abortController = new AbortController();
	const { getState, setState, when: whenState } = createState(opts?.startImmediately === false ? "paused" : "active");
	const { getState: getReason, setState: setReason } = createState(null);
	let _opts = {
		timeBetweenRetries: 3e3,
		...opts
	};
	let tries = 0;
	let data = args.initialData;
	function _log(...args$1) {
		if (!_opts.debug) return;
		console.debug("[poll]", ...args$1);
	}
	function assert() {
		if (getState() === "done") return true;
		const done = args.assertFn(data);
		if (done) {
			_log("Done!", data);
			setState("done");
			setReason("success");
		}
		return done;
	}
	function whenAborted() {
		return new Promise((resolve) => {
			if (opts?.signal?.aborted || abortController.signal.aborted) return resolve();
			const listener = () => {
				opts?.signal?.removeEventListener("abort", listener);
				abortController.signal.removeEventListener("abort", listener);
				resolve();
			};
			opts?.signal?.addEventListener("abort", listener);
			abortController.signal.addEventListener("abort", listener);
		});
	}
	function getResult() {
		return {
			data,
			state: getState(),
			reason: getReason()
		};
	}
	async function _loop() {
		_log("Loop!");
		if (getState() === "done") return getResult();
		if (getState() === "paused") {
			_log("Loop paused.");
			await Promise.race([whenState((s) => s === "active"), whenAborted()]);
		}
		if (abortController.signal.aborted) {
			_log("Aborted!");
			setState("done");
			setReason("aborted");
			return getResult();
		}
		try {
			data = await args.requestFn({ signal: opts?.signal });
			_log("Got new data", data);
			if (assert()) return getResult();
		} catch {
			if (opts?.signal?.aborted) {
				setState("done");
				setReason("aborted");
				return getResult();
			}
		}
		tries++;
		if (typeof opts?.maxTries === "number" && tries >= opts.maxTries) {
			_log("Max tries exceeded");
			setState("done");
			setReason("limit-exceeded");
			return getResult();
		}
		_log("Waiting before next loop...");
		await Promise.race([new Promise((r) => setTimeout(r, _opts.timeBetweenRetries)), whenAborted()]);
		return _loop();
	}
	const poller = {
		getState: () => ({
			state: getState(),
			reason: getReason(),
			data,
			tries
		}),
		start: () => {
			setState("active");
		},
		pause: () => {
			setState("paused");
		},
		abort: (reason) => {
			setState("done");
			setReason("aborted");
			abortController.abort(reason);
		},
		setOpts: (opts$1) => {
			_opts = { ...opts$1 };
		},
		result: async () => {
			if (assert()) return getResult();
			return _loop();
		}
	};
	if (_opts.startImmediately !== false) poller.start();
	return poller;
}

//#endregion
export { poll };